<!--
    Design: https://flowbite.com/docs/components/chat-bubble/
-->
<!-- Both panes reserve their scrollbar gutter ([scrollbar-gutter:stable]) so content doesn't jump
     sideways on Windows when a list grows past the pane and its classic scrollbar appears -
     most visibly the user's own right-aligned bubbles. Alternatives were worse: overflow:overlay
     is gone (Chrome 114 made it = auto), JS scrollbar libs would wrap #scroller and fight
     (scroll)="onChatScroll()", and "both-edges" wastes space on the left. The cost is an empty
     strip on Windows even when nothing overflows (macOS overlay scrollbars reserve nothing), so
     [scrollbar-width:thin] narrows it (~17px -> ~11px). The chat list drops its right padding
     (pr-0) so the gutter stands in for it instead of doubling up. -->
<!-- Edge-to-edge panes, chat-app style: spacing lives inside the panes, not around them.
     Below sm one pane shows at a time; from sm up the list takes a third (never under 18rem),
     the chat the rest. minmax(0,1fr) keeps the row at full height so the panes scroll inside
     rather than a long list stretching the page. max-w-7xl stops ultrawide screens stretching. -->
<div
  class="h-full w-full max-w-7xl mx-auto flex relative sm:grid sm:grid-cols-[minmax(18rem,1fr)_2fr] sm:grid-rows-[minmax(0,1fr)] xl:border-x border-gray-200"
>
  <div
    chat-list
    class="basis-full p-2 pr-0 overflow-y-auto [scrollbar-gutter:stable] [scrollbar-width:thin] sm:block sm:border-r border-gray-200"
    [ngClass]="{
      'hidden': !!selectedChat.value?.[0] 
    }"
  >
    <div mat-subheader>Chats</div>
    <ng-container>
      <mat-selection-list
        #chatsListing
        [multiple]="false"
        [formControl]="selectedChat"
      >
        <mat-list-option [value]="NATIONAL_CHAT_ROOM">
          <!--  -->
          <img
            matListItemAvatar
            ngSrc="https://placehold.co/50x50/4ade80/000/?text=corpers"
            width="50"
            height="50"
            alt="Profile pic"
          />
          <div matListItemTitle>General Chatroom</div>
          <div matListItemLine>
            {{
              snippet(appStateChats.get(NATIONAL_CHAT_ROOM)?.texts?.at(-1)?.message) ||
                'Start new chat...'
            }}
          </div>
          <div
            class="font-bold"
            *ngIf="appStateChats.get(NATIONAL_CHAT_ROOM)?.unread_messages"
            matListItemLine
          >
            {{ appStateChats.get(NATIONAL_CHAT_ROOM)!.unread_messages }} unread
            message<span
              *ngIf="appStateChats.get(NATIONAL_CHAT_ROOM)!.unread_messages > 1"
              >s</span
            >
          </div>
        </mat-list-option>

        <mat-divider></mat-divider>
        <ng-container *ngIf="appStateChats.size > 1; else noChatList">
          <ng-container *ngFor="let _chat of appStateChats | keyvalue; trackBy: trackByChatEntry">
            <ng-container *ngIf="_chat?.key !== NATIONAL_CHAT_ROOM">
              <!-- Wrapping in an object literal so `getOtherParticipant()` runs once per row
                   per change-detection pass instead of once per binding below. -->
              <ng-container *ngIf="{ participant: getOtherParticipant(_chat?.value) } as chatMeta">
                <mat-list-option [value]="_chat?.key">
                  <app-avatar
                    matListItemAvatar
                    class="w-10 h-10 text-sm"
                    [photoUrl]="chatMeta.participant?.Media?.url"
                    [entity]="chatMeta.participant"
                    [clickable]="chatMeta.participant | hasPublicProfile"
                    (activate)="openPublicProfile(chatMeta.participant)"
                    (click)="$event.stopPropagation()"
                  ></app-avatar>
                  <div matListItemTitle>
                    {{
                      _chat?.value?.chat_display_name?.trim() ||
                        _chat?.value?.chat_display_state_code ||
                        'Corp member'
                    }}
                  </div>
                  <div matListItemLine>
                    {{
                      snippet(_chat?.value?.texts?.at(-1)?.message) || 'Start new chat...'
                    }}
                  </div>
                  <div
                    class="font-bold"
                    *ngIf="_chat.value.unread_messages"
                    matListItemLine
                  >
                    {{ _chat.value.unread_messages }} unread message<span
                      *ngIf="_chat.value.unread_messages > 1"
                      >s</span
                    >
                  </div>
                </mat-list-option>
                <mat-divider></mat-divider>
              </ng-container>
            </ng-container>
          </ng-container>
        </ng-container>
      </mat-selection-list>
    </ng-container>

    <ng-template #noChatList>
      <!-- @todo: Maybe uncomment later... -->
      <!-- <mat-divider></mat-divider> -->
      <!-- <p class="pt-3">No personal chats.</p> -->
    </ng-template>
  </div>

  <ng-container *ngIf="!!selectedChat.value?.[0]; else noSelectedChat">
    <mat-card class="h-full basis-full min-w-0 !rounded-none !shadow-none">
      <!-- Mobile chat header: back icon | centered name/statecode | 3-dot menu -->
      <div
        card-header
        class="flex sm:hidden items-center border-b border-gray-300 px-1 py-1 gap-1"
      >
        <button
          type="button"
          (click)="closeCurrentChat()"
          mat-icon-button
          aria-label="Back to chats"
          matTooltip="Back"
        >
          <mat-icon>arrow_back</mat-icon>
        </button>

        <div class="flex flex-col items-center flex-1 min-w-0 overflow-hidden">
          <mat-card-title
            *ngIf="selectedChatRecipientName"
            class="!mb-0 !text-base w-full truncate text-center"
          >
            {{ selectedChatRecipientName }}
          </mat-card-title>
          <mat-card-subtitle
            *ngIf="selectedChatRecipientStateCode"
            class="!mb-0 w-full truncate text-center"
          >
            {{ selectedChatRecipientStateCode }}
          </mat-card-subtitle>
        </div>

        <button
          type="button"
          mat-icon-button
          [matMenuTriggerFor]="mobileChatOptionsMenu"
          color="primary"
          aria-label="Chat options"
          matTooltip="More options"
        >
          <mat-icon>more_vert</mat-icon>
        </button>
        <mat-menu #mobileChatOptionsMenu="matMenu">
          <button (click)="openBookmarkedMessages()" mat-menu-item>
            <mat-icon>bookmarks</mat-icon>
            <span>Bookmarked messages</span>
          </button>

          <ng-container *ngIf="selectedChat.value?.[0] === NATIONAL_CHAT_ROOM">
            <mat-divider></mat-divider>

            <button
              (click)="openCreateAccommodationPostDialog()"
              mat-menu-item
            >
              New Accommodation
            </button>

            <button (click)="openCreateSalePostDialog()" mat-menu-item>
              New Sale
            </button>

            <mat-divider></mat-divider>

            <button
              [routerLink]="['/search']"
              [queryParams]="{ tab: 'ppas' }"
              mat-menu-item
            >
              PPA Reviews
            </button>

            <!-- @todo: buy/sell and accommodation should take you to home page -->
            <button
              [routerLink]="['/search']"
              [queryParams]="{ tab: 'sales' }"
              mat-menu-item
            >
              Search Sales
            </button>
            <button
              [routerLink]="['/search']"
              [queryParams]="{ tab: 'accommodation' }"
              mat-menu-item
            >
              Find Accommodation
            </button>
          </ng-container>
        </mat-menu>
      </div>

      <!-- Desktop chat header -->
      <div
        card-header
        class="hidden sm:flex items-center justify-between border-b border-gray-300 px-4 py-2"
      >
        <div class="flex flex-col">
          <mat-card-title *ngIf="selectedChatRecipientName">
            {{ selectedChatRecipientName }}
          </mat-card-title>
          <mat-card-subtitle *ngIf="selectedChatRecipientStateCode">
            {{ selectedChatRecipientStateCode }}
          </mat-card-subtitle>
        </div>

        <div class="flex items-center gap-2">
          <ng-container *ngIf="selectedChat.value?.[0] === NATIONAL_CHAT_ROOM">
            <button
              type="button"
              mat-icon-button
              [matMenuTriggerFor]="chatOptionsMenu"
              color="primary"
              aria-label="Chat options"
              matTooltip="Chat options"
            >
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #chatOptionsMenu="matMenu">
              <!-- @todo: add these options for personal chats -->
              <!-- <button mat-menu-item>Mark as read</button>
            <button mat-menu-item>Block</button>
            <button mat-menu-item>Unblock</button>
            <button mat-menu-item>Report</button> -->

              <button
                (click)="openCreateAccommodationPostDialog()"
                mat-menu-item
              >
                New Accommodation
              </button>

              <button (click)="openCreateSalePostDialog()" mat-menu-item>
                New Sale
              </button>

              <mat-divider></mat-divider>

              <button
                [routerLink]="['/search']"
                [queryParams]="{ tab: 'ppas' }"
                mat-menu-item
              >
                PPA Reviews
              </button>

              <!-- @todo: buy/sell and accommodation should take you to home page -->
              <button
                [routerLink]="['/search']"
                [queryParams]="{ tab: 'sales' }"
                mat-menu-item
              >
                Search Sales
              </button>
              <button
                [routerLink]="['/search']"
                [queryParams]="{ tab: 'accommodation' }"
                mat-menu-item
              >
                Find Accommodation
              </button>
            </mat-menu>
          </ng-container>

          <button
            type="button"
            (click)="openBookmarkedMessages()"
            mat-icon-button
            color="primary"
            aria-label="Bookmarked messages"
            matTooltip="Bookmarked messages"
          >
            <mat-icon>bookmarks</mat-icon>
          </button>

          <button
            type="button"
            (click)="closeCurrentChat()"
            mat-icon-button
            class="!bg-slate-200 !inline-flex"
            color="primary"
            aria-label="Close chat"
            matTooltip="Close chat"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <mat-card-content class="overflow-y-auto [scrollbar-gutter:stable] [scrollbar-width:thin] flex-grow flex flex-col relative" #scroller (scroll)="onChatScroll()">
        <!-- Accommodation chat info card - sticky at top -->
        <div 
          *ngIf="showAccommodationNotification && selectedChat.value?.[0] && selectedChat.value?.[0] !== NATIONAL_CHAT_ROOM"
          class="sticky top-0 z-10 bg-white py-3 px-4 border-b border-gray-300"
        >
          <mat-card class="!bg-background-light !border !border-gray-300 !shadow-sm">
            <mat-card-content>
              <div class="flex items-start gap-3">
                <mat-icon class="text-primary">info</mat-icon>
                <span class="flex-1 min-w-0 text-primary text-sm font-normal">
                  This accommodation was posted by an outgoing corp member. If you inspect and decide to rent it, you agree to pay a ₦10,000 assistance fee. No payment is required if you don't take the accommodation.
                </span>
              </div>
              
            </mat-card-content>
            <mat-card-actions align="end">
              <button 
                  type="button"
                  mat-button
                  color="primary"
                  class="text-sm"
                  (click)="openAccommodationChatInfoDialog()"
                >
                  Learn more
                </button>
            </mat-card-actions>
          </mat-card>
        </div>
        
        <ng-container>
          <!-- Not a scroller itself: #scroller above is the one scroll container (the JS that
               loads older messages and scrolls to bottom reads it). An overflow here made this
               div shrink and scroll instead, leaving #scroller inert and stacking scrollbars. -->
          <div class="flex flex-col mt-auto w-full py-2.5">
            <!--
              Messages in a row from one sender on one day are a group: tighter together, and in the
              national chat only the first shows the name and photo. Each day starts with a separator,
              so a bubble only shows the time.
            -->
            <ng-container *ngFor="let item of selectedChatMessages | chatTimeline; trackBy: trackByTimelineItem">
              <ng-container *ngIf="{
                message: item.message,
                fromOther: item.message?.message_from !== myId,
                national: selectedChat.value?.[0] === NATIONAL_CHAT_ROOM
              } as m">
              <div *ngIf="item.dayLabel" class="flex justify-center mt-3 mb-1 first:mt-0" role="separator" [attr.aria-label]="item.dayLabel">
                <span class="rounded-full bg-gray-100 px-3 py-0.5 text-xs font-medium text-gray-600">{{ item.dayLabel }}</span>
              </div>
              <!-- mt-1 between bubbles of a group: enough of a gap to see where one ends. -->
              <div
                class="flex items-start gap-2"
                [class.mt-2.5]="item.groupStart && !item.dayLabel"
                [class.mt-1]="!item.groupStart"
              >
                <ng-container *ngIf="m.national && m.fromOther">
                  <app-avatar
                    *ngIf="item.groupStart; else avatarSpace"
                    class="size-5 text-[9px]"
                    [photoUrl]="m.message?.is_admin_post ? 'assets/images/logo_transparent_square.png' : m.message?.FromCorpMember?.Media?.url"
                    [entity]="m.message?.FromCorpMember"
                    [square]="!!m.message?.is_admin_post"
                    [clickable]="!m.message?.is_admin_post && (m.message?.FromCorpMember | hasPublicProfile)"
                    [tabbable]="false"
                    (activate)="openPublicProfile(m.message?.FromCorpMember)"
                  ></app-avatar>
                  <!-- Keeps the rest of the group's bubbles in line with the first. -->
                  <ng-template #avatarSpace><div class="size-5 shrink-0"></div></ng-template>
                </ng-container>
                <!-- A plain div, not a mat-card: a card's padding, hover shadow and typography all
                     had to be overridden to look like a chat bubble. Where two bubbles of a group
                     meet, their corners on the sender's side are tighter (rounded-md) than the outer
                     ones, and the shadow is tight so it doesn't fill the gap between them. -->
                <div
                  *ngIf="m.message as message"
                  [id]="'msg-' + message.id"
                  appChatBubbleGesture
                  (swipeReply)="setReplyTarget(message)"
                  (longPress)="messageMenuTrigger.openMenu()"
                  class="relative w-fit max-w-[75%] rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.12)] text-sm text-gray-900 transition-opacity duration-200"
                  [class.opacity-60]="message.pending"
                  [class.ring-1]="message.send_status === 'failed'"
                  [class.ring-red-600]="message.send_status === 'failed'"
                  [class.ring-2]="highlightedMessageId === message.id"
                  [class.ring-primary]="highlightedMessageId === message.id"
                  [ngClass]="{
                    'rounded-ss-none': m.national && m.fromOther && item.groupStart,
                    'rounded-ss-md': m.fromOther && !item.groupStart,
                    'rounded-es-md': m.fromOther && !item.groupEnd,
                    'rounded-se-md': !m.fromOther && !item.groupStart,
                    'rounded-ee-md': !m.fromOther && !item.groupEnd,
                    'text-right ml-auto bg-[#b9f3bb]': !m.fromOther,
                    'text-left bg-[#fdd835]': m.fromOther
                  }"
                >
                  <!--
                    Not a visible/clickable trigger — matMenuTriggerFor's own (click) listener
                    would otherwise open this on any plain tap of the bubble, not just a
                    long-press. openMenu() is only ever called programmatically from
                    (longPress) above, so this just needs to exist as an anchor point.
                  -->
                  <span
                    #messageMenuTrigger="matMenuTrigger"
                    [matMenuTriggerFor]="messageActionsMenu"
                    [matMenuTriggerData]="{ message: message }"
                    class="absolute inset-0 pointer-events-none opacity-0"
                    aria-hidden="true"
                  ></span>
                  <ng-container *ngIf="m.national && m.fromOther && item.groupStart">
                    <!-- The name opens the public profile too, like the avatar beside it. -->
                    <div class="text-xs font-bold py-1 px-3 border-b border-yellow-300">
                      <button
                        *ngIf="!message?.is_admin_post && (message?.FromCorpMember | hasPublicProfile); else plainName"
                        type="button"
                        class="font-bold text-left hover:underline focus-visible:underline"
                        [attr.aria-label]="'View ' + (message?.FromCorpMember | displayName) + '\'s profile'"
                        (click)="$event.stopPropagation(); openPublicProfile(message?.FromCorpMember)"
                      >{{ message?.FromCorpMember | displayName }}</button>
                      <ng-template #plainName>{{
                        message?.is_admin_post ? 'Admin - corpers.ng' : (message?.FromCorpMember | displayName)
                      }}</ng-template>
                    </div>
                  </ng-container>
                  <!-- What the message is about: the first message of a chat started from a listing. -->
                  <app-chat-listing-card
                    *ngIf="message.Listing"
                    class="block mx-1.5 mt-1.5"
                    [listing]="message.Listing"
                  ></app-chat-listing-card>
                  <div
                    *ngIf="message.ReplyTo"
                    class="mx-3 mt-2 px-2 py-1 border-l-4 border-primary bg-black/5 rounded-r text-xs text-left cursor-pointer"
                    (click)="$event.stopPropagation(); scrollToMessage(message.ReplyTo.id)"
                  >
                    <div class="font-bold">
                      {{ message.ReplyTo.FromCorpMember | displayName }}
                    </div>
                    <div class="truncate">
                      <ng-container *ngFor="let segment of message.ReplyTo.message | parseMentions">{{
                        segment.type === 'mention' ? '@' + segment.value : segment.value
                      }}</ng-container>
                    </div>
                  </div>
                  <!--
                    px-3 lines up with the name and the time. text-left keeps the user's own
                    multi-line messages from being ragged on the left, and break-words only breaks a
                    long word or link when it can't fit on its own line (break-all split links
                    mid-word even when they'd fit). Messages are trimmed when sent (here and by the
                    backend); the trim here is for older ones, whose trailing newline pre-wrap would
                    show as a blank line.
                  -->
                  <div class="px-3 pt-1.5 pb-0.5 text-left whitespace-pre-wrap break-words">
                    <ng-container *ngFor="let segment of message.message?.trim() | parseMentions">
                      <span
                        *ngIf="segment.type === 'mention'; else linkOrText"
                        class="mention-tag"
                        role="button"
                        tabindex="0"
                        (click)="$event.stopPropagation(); openMentionProfile(segment)"
                        (keydown.enter)="openMentionProfile(segment)"
                        >@{{ segment.value }}</span
                      >
                      <ng-template #linkOrText>
                        <a
                          *ngIf="segment.type === 'link'; else plainText"
                          href="javascript:void(0)"
                          class="font-bold text-primary hover:underline"
                          role="button"
                          tabindex="0"
                          (click)="$event.stopPropagation(); confirmOpenLink(segment.url)"
                          (keydown.enter)="confirmOpenLink(segment.url)"
                          >{{ segment.value }}</a
                        >
                        <ng-template #plainText>{{ segment.value }}</ng-template>
                      </ng-template>
                    </ng-container>
                  </div>

                  <div class="text-[10px] px-3 pb-1 text-[#101010]">
                    <ng-container *ngIf="message.pending; else showTimestamp" [ngSwitch]="message.send_status">
                      <button
                        *ngSwitchCase="'failed'"
                        type="button"
                        class="inline-flex items-center gap-1 font-semibold text-red-700"
                        (click)="$event.stopPropagation(); retryMessage(message)"
                      >
                        <mat-icon class="!size-3 !text-[12px] !leading-3">error_outline</mat-icon>
                        Not sent. Tap to retry
                      </button>
                      <span *ngSwitchCase="'waiting'" class="inline-flex items-center gap-1 italic text-gray-500">
                        <mat-icon class="!size-3 !text-[12px] !leading-3">schedule</mat-icon>
                        Waiting for network
                      </span>
                      <span *ngSwitchDefault class="flex items-center gap-1 italic text-gray-500">
                        <mat-spinner diameter="10"></mat-spinner>
                        Sending...
                      </span>
                    </ng-container>
                    <!-- Just the time: the day is in the separator above (and in the tooltip). -->
                    <ng-template #showTimestamp>
                      <span class="inline-flex items-center gap-0.5">
                        <time [attr.datetime]="message.created_at" [title]="message.created_at | date : 'EEE d MMM y, h:mm a'">{{
                          message.created_at | date : 'h:mm a'
                        }}</time>
                        <!-- Read receipts on your own messages in a direct chat: ✓ sent, ✓✓ read. -->
                        <ng-container *ngIf="!m.fromOther && !m.national">
                          <mat-icon
                            *ngIf="message.time_read; else sentTick"
                            class="!size-3.5 !text-[14px] !leading-[14px] text-sky-700"
                            aria-hidden="false"
                            aria-label="Read"
                            [title]="'Read ' + (message.time_read | date : 'EEE d MMM, h:mm a')"
                          >done_all</mat-icon>
                          <ng-template #sentTick>
                            <mat-icon class="!size-3.5 !text-[14px] !leading-[14px] text-gray-500" aria-hidden="false" aria-label="Sent" title="Sent">done</mat-icon>
                          </ng-template>
                        </ng-container>
                      </span>
                    </ng-template>
                  </div>
                </div>
              </div>
              </ng-container>
            </ng-container>
          </div>
        </ng-container>

        <mat-menu #messageActionsMenu="matMenu">
          <ng-template matMenuContent let-message="message">
            <!-- An unsent message: waiting for the network, or "Not sent" (a sending one has no menu). -->
            <ng-container *ngIf="message.pending && message.send_status !== 'sending'">
              <button *ngIf="message.send_status === 'failed'" mat-menu-item (click)="retryMessage(message)">
                <mat-icon>refresh</mat-icon>
                <span>Retry</span>
              </button>
              <button mat-menu-item (click)="editUnsentMessage(message)">
                <mat-icon>edit</mat-icon>
                <span>Edit</span>
              </button>
              <button mat-menu-item (click)="deleteUnsentMessage(message)">
                <mat-icon>delete</mat-icon>
                <span>Delete</span>
              </button>
            </ng-container>
            <ng-container *ngIf="!message.pending">
              <button mat-menu-item (click)="setReplyTarget(message)">
                <mat-icon>reply</mat-icon>
                <span>Reply</span>
              </button>
              <button mat-menu-item (click)="toggleBookmark(message)">
                <mat-icon>{{ bookmarkedMessageIds.has(message.id) ? 'bookmark' : 'bookmark_border' }}</mat-icon>
                <span>{{ bookmarkedMessageIds.has(message.id) ? 'Remove bookmark' : 'Bookmark' }}</span>
              </button>
            </ng-container>
          </ng-template>
        </mat-menu>
      </mat-card-content>

      <div
        *ngIf="replyingTo"
        class="flex items-start justify-between gap-2 mx-2.5 mt-2 px-2 py-1.5 border-l-4 border-primary bg-black/5 rounded-r text-xs"
      >
        <div class="min-w-0">
          <div class="font-bold">
            {{ replyingTo.FromCorpMember | displayName }}
          </div>
          <div class="truncate">
            <ng-container *ngFor="let segment of replyingTo.message | parseMentions">{{
              segment.type === 'mention' ? '@' + segment.value : segment.value
            }}</ng-container>
          </div>
        </div>
        <button type="button" mat-icon-button aria-label="Cancel reply" (click)="cancelReply()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div *ngIf="isOffline" class="flex items-center gap-2 px-2.5 py-1.5 text-sm bg-amber-50 text-amber-800 border-t">
        <mat-icon class="!text-base" aria-hidden="true">cloud_off</mat-icon>
        <span>You're offline. Messages will send once you're back online.</span>
      </div>
      <mat-card-footer class="flex py-2.5 px-2.5 pt-0 border-t">
        <div class="w-[calc(100%-50px)] flex-1">
          <app-mention-textarea [formControl]="chatMessage"></app-mention-textarea>
        </div>
        <button
          type="button"
          class="mb-[22px]"
          [disabled]="!chatMessage.value"
          mat-icon-button
          color="primary"
          aria-label="Send message icon"
          (click)="sendMessage()"
        >
          <mat-icon>send</mat-icon>
        </button>
      </mat-card-footer>
      <mat-card-footer *ngIf="isSendingMessage">
        <!-- show progress of sending message -->
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      </mat-card-footer>

      <!-- <mat-card-actions></mat-card-actions> -->
    </mat-card>
  </ng-container>
  <ng-template #noSelectedChat>
    <div
      class="hidden basis-full sm:flex gap-4 flex-col justify-center items-center h-full text-center bg-slate-50"
    >
      <div *ngIf="appStateChats.size > 0; else noChats">
        <h3 class="text-lg">Chat with corp members across Nigeria.</h3>
        <p class="text-sm">Select a chat to start.</p>
      </div>
      <ng-template #noChats>
        <h3 class="text-lg">Your chats will appear here.</h3>
        <p class="text-sm">Start a chat in the General Chatroom.</p>
      </ng-template>

      <mat-icon color="primary" class="scale-150">forum</mat-icon>
    </div>
  </ng-template>
</div>
